/* global google */
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import { openAreaTray } from '@actions/map-actions';
import { mapConfig } from '@constants/component-configs';
import { getTrayLayer, getHoverLayerId, getTrayLayersIds, isAnyTrayItemHovered } from '@selectors/map-selector';
import MarkerIcon from '@shared/marker-icon';
import Polygon from '@shared/polygon';
import Polyline from '@shared/polyline';
import { getLayerIcon, getMarkerAnchorStyle } from '@utils/icon-utils';
import { generatePath, generatePolygonPaths, getPointShapeCoords } from '@utils/map-utils';
import { shallowEqual } from '@utils/react-utils';
import { isLayerMarkerVisible } from '@utils/markers-utils';

/**
 * Calculate if layer should be highlighted based on tray state.
 * @param {object} params - Parameters.
 * @returns {boolean} Whether the layer should be highlighted.
 */
const shouldHighlight = ({ layerId, trayEntity, trayHoverId }) => {
  return layerId && (
    (trayEntity && trayEntity.id && layerId === trayEntity.id) ||
    (trayHoverId && layerId === trayHoverId)
  );
};

/**
 * Custom comparison function for React.memo.
 * @param {object} prevProps - Previous props.
 * @param {object} nextProps - Next props.
 * @returns {boolean} Whether props are equal (true = don't re-render).
 */
const arePropsEqual = (prevProps, nextProps) => {
  // If any core property changed, re-render
  if (
    !shallowEqual(nextProps.layer?.id, prevProps.layer?.id) ||
    !shallowEqual(nextProps.icon, prevProps.icon) ||
    !shallowEqual(nextProps.layer, prevProps.layer) ||
    !shallowEqual(nextProps.mapStyle, prevProps.mapStyle) ||
    !shallowEqual(nextProps.markerVisible, prevProps.markerVisible) ||
    !shallowEqual(nextProps.styleCondition, prevProps.styleCondition)
  ) {
    return false;
  }

  // For tray-related properties, check if highlight status changed
  if (!shallowEqual(nextProps.isAnyTrayItemHovered, prevProps.isAnyTrayItemHovered) ||
      !shallowEqual(nextProps.trayEntity, prevProps.trayEntity) ||
      !shallowEqual(nextProps.trayItemIds, prevProps.trayItemIds) ||
      !shallowEqual(nextProps.trayHoverId, prevProps.trayHoverId)) {
    const nextIsHighlighted = shouldHighlight({
      layerId: nextProps.layer?.id,
      trayEntity: nextProps.trayEntity,
      trayHoverId: nextProps.trayHoverId
    }) || false;
    const prevIsHighlighted = shouldHighlight({
      layerId: prevProps.layer?.id,
      trayEntity: prevProps.trayEntity,
      trayHoverId: prevProps.trayHoverId
    }) || false;
    return shallowEqual(nextIsHighlighted, prevIsHighlighted);
  }

  return true;
};

const Figure = ({
  icon,
  layer,
  mapStyle,
  markerVisible,
  name,
  styleCondition,
  trayEntity,
  trayHoverId
}) => {
  const dispatch = useDispatch();

  const isHighlighted = useMemo(
    () => shouldHighlight({ layerId: layer?.id, trayEntity, trayHoverId }),
    [layer?.id, trayEntity, trayHoverId]
  );

  const onMouseMove = useCallback(event => {
    window.highlightCircle.setCenter(event.latLng);
  }, []);

  // AdvancedMarker's mouse events carry a raw DOM MouseEvent (no latLng), so use the
  // marker's own known position instead of reading it off the event.
  const onMarkerMouseEnter = useCallback(() => {
    window.highlightCircle.setCenter(getPointShapeCoords(layer.shape));
  }, [layer.shape]);

  const pointClick = useCallback(() => {
    const { shape } = layer;
    if (shape.type === 'Point') {
      dispatch(openAreaTray(getPointShapeCoords(shape)));
    }
  }, [dispatch, layer]);

  const getStyle = useCallback(() => {
    const { attrs } = layer;
    if (styleCondition) {
      const styles = mapStyle[attrs[styleCondition]];
      if (styles) {
        return cloneDeep(styles);
      }
    }
    return cloneDeep(mapStyle);
  }, [layer, mapStyle, styleCondition]);

  const computedMapStyle = useMemo(() => {
    const style = getStyle();
    if (!style) {
      return {};
    }
    if (isHighlighted) {
      style.zIndex = 210;
      if (style.strokeWeight) {
        style.strokeWeight = style.strokeWeight + 3;
      }
      if (style.fillColor) {
        style.fillOpacity = 0.6;
      }
      if (style.icons && style.icons.length > 0) {
        style.icons[0].icon.scale = 1.5;
        style.icons[0].repeat = '12px';
      }
    } else if (style.fillColor) {
      // Restore opacity if not highlighting
      style.fillOpacity = 0.3;
    }
    return style;
  }, [getStyle, isHighlighted]);

  if (!markerVisible) {
    return null;
  }

  const { id, shape } = layer;
  const figureId = `${name}-${id}`;
  const layerProps = {
    options: { ...computedMapStyle, clickable: false },
    onMouseMove,
    onMouseOver: onMouseMove
  };

  if (shape.type === 'Point') {
    const layerIcon = getLayerIcon(icon, isHighlighted ? 15 : 0);
    return (
      <AdvancedMarker
        key={figureId}
        onClick={pointClick}
        onMouseEnter={onMarkerMouseEnter}
        position={getPointShapeCoords(shape)}
        title={name}
        zIndex={isHighlighted ? 210 : 199}
        {...getMarkerAnchorStyle(layerIcon)}
      >
        <MarkerIcon icon={layerIcon} />
      </AdvancedMarker>
    );
  }

  if (shape.type === 'LineString') {
    const path = generatePath(shape.coordinates);
    const lines = [<Polyline key={figureId} path={path} {...layerProps} />];
    if (isHighlighted) {
      lines.push(<Polyline key={`${figureId}-1`} path={path} options={mapConfig.hoverStyle.middle} />);
      lines.push(<Polyline key={`${figureId}-2`} path={path} options={mapConfig.hoverStyle.outer} />);
    }
    return lines;
  }

  if (shape.type === 'MultiLineString') {
    const lines = [];
    shape.coordinates.forEach((coords, index) => {
      const path = generatePath(coords);
      lines.push(<Polyline key={`${figureId}-${index}`} path={path} {...layerProps} />);
      if (isHighlighted) {
        lines.push(<Polyline key={`${figureId}-${index}-1`} path={path} options={mapConfig.hoverStyle.middle} />);
        lines.push(<Polyline key={`${figureId}-${index}-2`} path={path} options={mapConfig.hoverStyle.outer} />);
      }
    });
    return lines;
  }

  if (shape.type === 'Polygon') {
    const polygonPath = generatePolygonPaths(shape.coordinates);
    const polygonOutlinePath = generatePath(shape.coordinates[0]);
    if (isHighlighted) {
      return (
        <div>
          <Polygon key={figureId} paths={polygonPath} {...layerProps} />
          <Polygon key={`${figureId}-1`}
            paths={polygonPath}
            options={{...mapConfig.polygonHoverStyle.middle, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />
          <Polygon key={`${figureId}-2`}
            paths={polygonPath}
            options={{...mapConfig.polygonHoverStyle.outer, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />
          {computedMapStyle && Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
            <Polyline key={`outline-${figureId}`} path={polygonOutlinePath} {...layerProps} />
          }
        </div>
      );
    }
    return (
      <div>
        <Polygon key={figureId} paths={polygonPath} {...layerProps} />
        {computedMapStyle &&
          Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
          <Polyline key={`outline-${figureId}`} path={generatePath(shape.coordinates[0])} {...layerProps} />
        }
      </div>
    );
  }

  if (shape.type === 'MultiPolygon') {
    if (isHighlighted) {
      const polygons = [];
      shape.coordinates.forEach((coords, index) => {
        const polygonPath = generatePolygonPaths(coords);
        const polygonOutlinePath = generatePath(coords[0]);
        polygons.push(<div key={`wrapper-${figureId}-${index}`}>
          <Polygon key={figureId} paths={polygonPath} {...layerProps} />
          <Polygon key={`${figureId}-${index}-1`}
            paths={polygonPath}
            options={{...mapConfig.polygonHoverStyle.middle, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />
          <Polygon key={`${figureId}-${index}-2`}
            paths={polygonPath}
            options={{...mapConfig.polygonHoverStyle.outer, strokePosition: google.maps.StrokePosition.OUTSIDE}}
          />
          {computedMapStyle && Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
            <Polyline key={`outline-${figureId}-${index}`} path={polygonOutlinePath} {...layerProps} />
          }
        </div>);
      });
      return polygons;
    }
    return shape.coordinates.map((coords, index) => (
      <div key={`wrapper-${figureId}-${index}`}>
        <Polygon
          key={`${figureId}-${index}`}
          paths={generatePolygonPaths(coords)}
          {...layerProps}
        />
        {computedMapStyle && Object.prototype.hasOwnProperty.call(computedMapStyle, 'icons') &&
          <Polyline
            key={`outline-${figureId}-${index}`}
            path={generatePath(coords[0])}
            {...layerProps}
          />
        }
      </div>
    ));
  }

  return null;
};

Figure.propTypes = {
  icon: PropTypes.string,
  isAnyTrayItemHovered: PropTypes.bool,
  layer: PropTypes.object,
  mapStyle: PropTypes.object,
  markerVisible: PropTypes.bool,
  name: PropTypes.string,
  styleCondition: PropTypes.string,
  trayEntity: PropTypes.object,
  trayHoverId: PropTypes.number,
  trayItemIds: PropTypes.object
};

const MemoizedFigure = memo(Figure, arePropsEqual);

// Container component that handles Redux connection
const FigureContainer = ({ layer, name }) => {
  const layers = useSelector(state => state.layers);
  const trayEntity = useSelector(getTrayLayer);
  const trayHoverId = useSelector(getHoverLayerId);
  const trayItemIds = useSelector(getTrayLayersIds);
  const isAnyHovered = useSelector(isAnyTrayItemHovered);
  const markerVisible = useSelector(state => isLayerMarkerVisible(state, name, layer));

  const {
    icon,
    map_style: mapStyle,
    style_condition: styleCondition
  } = layers[name];

  return (
    <MemoizedFigure
      icon={icon}
      isAnyTrayItemHovered={isAnyHovered}
      layer={layer}
      mapStyle={mapStyle}
      markerVisible={markerVisible}
      name={name}
      styleCondition={styleCondition}
      trayEntity={trayEntity}
      trayHoverId={trayHoverId}
      trayItemIds={trayItemIds}
    />
  );
};

FigureContainer.propTypes = {
  layer: PropTypes.object,
  name: PropTypes.string
};

export default FigureContainer;
